import { readRequiredAuthMemberRoleArray } from "@airange/shared/auth-member-roles";
import type { operations } from "@airange/generated-contracts/redblue";

export type RedblueOperation<Name extends keyof operations> = operations[Name];

export type OperationQuery<Name extends keyof operations> =
  RedblueOperation<Name> extends {
    parameters: { query?: infer Query };
  }
    ? NonNullable<Query>
    : never;

export type RedblueCurrentUser = NonNullable<
  RedblueOperation<"bootstrap_auth_session_api_auth__application_key__bootstrap_post">["responses"][200]["content"]["application/json"]
>;

export type RedblueApplicationRole = RedblueCurrentUser["application_roles"][number];
export type RedblueApplicationRoleIdentity = Pick<RedblueCurrentUser, "application_roles">;

const REDBLUE_APPLICATION_ROLE_KEYS = [
  "portal_member",
  "exercise_manager",
  "platform_manager",
] as const satisfies readonly RedblueApplicationRole[];

export type RedblueDisplayUser = RedblueCurrentUser & {
  username: string;
  full_name?: string | null;
  avatar_url?: string | null;
};

export function readRedblueCurrentUserApplicationRoles(
  currentUser: RedblueApplicationRoleIdentity,
  path = "current_user.application_roles",
): RedblueApplicationRole[] {
  return readRequiredAuthMemberRoleArray(currentUser, {
    allowedRoles: REDBLUE_APPLICATION_ROLE_KEYS,
    path,
  });
}

export function toRedblueDisplayUser(user: RedblueCurrentUser): RedblueDisplayUser {
  return {
    ...user,
    username: user.username ?? user.user_id,
    full_name: user.display_name ?? user.username ?? user.user_id,
  };
}

export type RedblueMemberListApiQuery = OperationQuery<"list_members_api_members_get">;

export type RedblueMemberListRead =
  RedblueOperation<"list_members_api_members_get">["responses"][200]["content"]["application/json"];

export type RedblueMemberRead = RedblueMemberListRead["items"][number];

export type RedblueMemberBulkCreate =
  RedblueOperation<"bulk_add_members_api_members_post">["requestBody"]["content"]["application/json"];

export type RedblueMemberAddItem = RedblueMemberBulkCreate["items"][number];

export type RedblueMemberRoleUpdate =
  RedblueOperation<"update_member_roles_api_members__member_id__roles_put">["requestBody"]["content"]["application/json"];

export type RedblueRoleCatalogRead =
  RedblueOperation<"get_role_catalog_api_platform_role_catalog_get">["responses"][200]["content"]["application/json"];

export type RedblueRoleCatalogItem = RedblueRoleCatalogRead["roles"][number];

export type RedblueDirectoryPickerBootstrap =
  RedblueOperation<"get_identity_directory_picker_bootstrap_api_platform_identity_directory_picker_bootstrap_get">["responses"][200]["content"]["application/json"];

export type RedblueDirectoryPickerUserSearchQuery =
  RedblueOperation<"search_identity_directory_picker_users_api_platform_identity_directory_picker_users_search_post">["requestBody"]["content"]["application/json"];

export type RedblueDirectoryPickerUserSearchResponse =
  RedblueOperation<"search_identity_directory_picker_users_api_platform_identity_directory_picker_users_search_post">["responses"][200]["content"]["application/json"];

export type RedblueIdentityUserListRead =
  RedblueOperation<"list_identity_users_by_ids_api_platform_identity_users_batch_get">["responses"][200]["content"]["application/json"];

export type RedblueIdentityUserRead = RedblueIdentityUserListRead["items"][number];
